import { ServiceError } from "../errors.js";
import {
  createSupabaseStorageClient,
  supabaseConfigFromEnv,
  type SupabaseStorageConfig,
} from "../releases/storage.js";

export const DIAGNOSTICS_BUCKET = "diagnostic-bundles";
export const DIAGNOSTICS_SIGNED_URL_TTL_SECONDS = 600;

/** A storage failure here is a dependency outage — an unprovisioned bucket, an
 *  unreachable Supabase, a key without write scope — never a malformed request.
 *  Raised as a bare `Error` it fell through `errorResponse` into the generic
 *  400 `bad_request` envelope, so the desktop reported an uninterpretable
 *  `service client error 400` for a server-side problem. 503 carries the
 *  codebase's "retrying later can succeed" meaning (see `ServiceErrorStatus`),
 *  and `reason` names the upstream cause. The object path stays out of the
 *  envelope: it embeds tenant/user/device ids and adds nothing to the diagnosis. */
export function diagnosticsStorageError(
  operation: DiagnosticsStorageOperation,
  reason: string,
): ServiceError {
  return new ServiceError("diagnostics_storage_error", 503, {
    operation,
    reason,
  });
}

export type DiagnosticsStorageOperation = "upload" | "sign" | "delete";

/** supabase-js reports most failures through the returned `{ error }`, but a
 *  transport fault it cannot classify is re-thrown instead — and an unmapped
 *  throw lands back in the generic 400 `bad_request` envelope this module
 *  exists to avoid. Every operation runs through here so both channels answer
 *  with the same code. */
export async function withDiagnosticsStorageErrors<T>(
  operation: DiagnosticsStorageOperation,
  run: () => Promise<T>,
): Promise<T> {
  try {
    return await run();
  } catch (error) {
    if (error instanceof ServiceError) throw error;
    throw diagnosticsStorageError(
      operation,
      error instanceof Error ? error.message : String(error),
    );
  }
}

export interface DiagnosticsStorage {
  uploadObject(objectPath: string, bytes: Uint8Array): Promise<void>;
  createSignedUrl(objectPath: string, ttlSeconds: number): Promise<string>;
  deleteObject(objectPath: string): Promise<void>;
}

export function createSupabaseDiagnosticsStorage(
  config: SupabaseStorageConfig,
): DiagnosticsStorage {
  const client = createSupabaseStorageClient(config);
  const bucket = config.bucket ?? DIAGNOSTICS_BUCKET;
  return {
    uploadObject(objectPath, bytes) {
      return withDiagnosticsStorageErrors("upload", async () => {
        const { error } = await client.storage
          .from(bucket)
          .upload(objectPath, Buffer.from(bytes), {
            upsert: false,
            contentType: "application/zip",
          });
        if (error) throw diagnosticsStorageError("upload", error.message);
      });
    },
    createSignedUrl(objectPath, ttlSeconds) {
      return withDiagnosticsStorageErrors("sign", async () => {
        const { data, error } = await client.storage
          .from(bucket)
          .createSignedUrl(objectPath, ttlSeconds);
        if (error || !data?.signedUrl) {
          throw diagnosticsStorageError("sign", error?.message ?? "no url");
        }
        return data.signedUrl;
      });
    },
    deleteObject(objectPath) {
      return withDiagnosticsStorageErrors("delete", async () => {
        const { error } = await client.storage
          .from(bucket)
          .remove([objectPath]);
        if (error) throw diagnosticsStorageError("delete", error.message);
      });
    },
  };
}

export function diagnosticsStorageFromEnv(
  env: NodeJS.ProcessEnv = process.env,
): DiagnosticsStorage | undefined {
  const config = supabaseConfigFromEnv(env);
  return config ? createSupabaseDiagnosticsStorage(config) : undefined;
}

export class InMemoryDiagnosticsStorage implements DiagnosticsStorage {
  readonly objects = new Map<string, Uint8Array>();

  async uploadObject(objectPath: string, bytes: Uint8Array): Promise<void> {
    this.objects.set(objectPath, bytes);
  }

  async createSignedUrl(
    objectPath: string,
    ttlSeconds: number,
  ): Promise<string> {
    // The double raises the same coded failure as the Supabase implementation
    // so a test that signs a missing object exercises the real envelope.
    if (!this.objects.has(objectPath))
      throw diagnosticsStorageError("sign", "object not found");
    return `memory://${objectPath}?ttl=${ttlSeconds}`;
  }

  async deleteObject(objectPath: string): Promise<void> {
    this.objects.delete(objectPath);
  }
}
